import {
  faUsers,
  faStore,
  faListAlt,
  faFileAlt,
  faUserLarge,
  faChartLine,
  faChartColumn,
  faMailBulk,
  faListSquares,
  faTachometer,
  faCodeMerge,
  faFlag,
  faInbox,
  faGear,
} from "@fortawesome/free-solid-svg-icons";

import stores from "../../../assets/images/brands.svg";
import groups from "../../../assets/images/groups.svg";
import performance from "../../../assets/images/performance.svg";
import userPermission from "../../../assets/images/user-permission.svg";
import themeSetting from "../../../assets/images/theme-setting.svg";
import surveyQuestion from "../../../assets/images/survey-question.svg";
import archivedQuestions from "../../../assets/images/archived-question.svg";

import {
  Svg,
  LoginRoles,
  emptyString,
  pathConstant,
  viewPathConstants,
  sidebarScreenConstants,
  featureFlagsEnums,
  screenPathsConstantsWithOutParams,
  adminPath,
  loginConstants,
  EMPTY_ARRAY_LENGTH,
} from "../../../constants";
import {
  npsFirstFeatureStatusEnums,
  userPermissionsConstants,
} from "../../../views/shared-screens/constants";
import { getItemFromStorage } from "../../../system";

const {
  DASHBOARD_PATH,
  GROUP_INBOX_PATH,
  BRAND_INBOX_PATH,
  BRAND_GROUPS_PATH,
  BRAND_PROFILE_PATH,
  STORE_PROFILE_PATH,
  GROUP_PROFILE_PATH,
  MANAGER_INBOX_PATH,
  GROUP_PROFILE_PAGE,
  GROUP_MANAGERS_PATH,
  MANAGER_PROFILE_PATH,
  GROUP_PERFORMANCE_PATH,
  STORE_PERFORMANCE_PATH,
  BRAND_PERFORMANCE_PATH,
  GROUP_USER_PERMISSIONS,
  GROUP_PERFORMANCE_PAGE,
  MANAGER_PERFORMANCE_PATH,
  MANAGER_USER_PERMISSIONS,
  BRAND_THEME_SETTINGS_PATH,
  DASHBOARD_FROM_STORES_PATH,
  BRAND_SURVEY_QUESTIONS_PATH,
  GROUP_SURVEY_QUESTIONS_PATH,
  BRAND_ARCHIVED_QUESTIONS_PATH,
  MANAGER_SURVEY_QUESTIONS_PATH,
} = screenPathsConstantsWithOutParams;

//generate image path when coming from hub
export const generateImagePath = (imagePathFromHub, path) => {
  return imagePathFromHub ? `/${imagePathFromHub}${path}` : path;
};

//Super admin and Brand admin default screen nav link
const getNavLinks = (role) => {
  if (role === userPermissionsConstants.BRAND_ADMIN) {
    return [
      {
        path: viewPathConstants.STORES,
        name: sidebarScreenConstants.RESTAURANTS,
        icon: faStore,
      },
      {
        isDropdown: true,
        name: sidebarScreenConstants.UTILITIES,
        icon: faFileAlt,
        subMenu: [
          {
            path: viewPathConstants.USER_PERMISSIONS,
            name: sidebarScreenConstants.USER_PERMISSIONS,
            icon: faUserLarge,
          },
        ],
      },
    ];
  } else {
    return [
      {
        path: viewPathConstants.STORES,
        name: sidebarScreenConstants.RESTAURANTS,
        icon: faStore,
      },
      {
        path: viewPathConstants.SUPER_ADMIN_PERFORMANCE,
        name: sidebarScreenConstants.PERFORMANCE,
        icon: faChartColumn,
      },
      {
        isDropdown: true,
        name: sidebarScreenConstants.UTILITIES,
        icon: faFileAlt,
        subMenu: [
          {
            path: viewPathConstants.USER_PERMISSIONS,
            name: sidebarScreenConstants.USER_PERMISSIONS,
            icon: faUserLarge,
          },
          {
            path: adminPath.FEATURE_FLAGS,
            name: sidebarScreenConstants.FEATURE_FLAGS,
            icon: faFlag,
          },
          {
            path: viewPathConstants.ACTIVITY_LOGS,
            name: sidebarScreenConstants.ACTIVITY_LOGS,
            icon: faListSquares,
          },
          {
            path: adminPath.SURVEY_QUEUES,
            name: sidebarScreenConstants.SURVEY_QUEUES,
            icon: faInbox,
          },
          {
            path: adminPath.MERGE_DUPLICATE_STORES,
            name: sidebarScreenConstants.MERGE_DUPLICATE_STORES,
            icon: faCodeMerge,
          },
          {
            path: adminPath.RESTAURANTS_BRAND_CONFIGURATION,
            name: sidebarScreenConstants.BRAND_CONFIGURATION,
            icon: faGear,
          },
        ],
      },
    ];
  }
};

//Super and Brand admin nav links
const getBrandNavLinks = ({
  role,
  brandId,
  imagePathFromHub,
  allBrandsWithFeatureFlags,
}) => {
  const navLinks = [
    {
      path: `${DASHBOARD_FROM_STORES_PATH}/${brandId}`,
      name: sidebarScreenConstants.DASHBOARD,
      icon: faChartLine,
    },
    {
      isDropdown: true,
      name: sidebarScreenConstants.SURVEY_MENU,
      icon:
        role === userPermissionsConstants.BRAND_ADMIN
          ? faTachometer
          : faListAlt,
      subMenu: [
        {
          path: `${BRAND_PROFILE_PATH}/${brandId}`,
          name: sidebarScreenConstants.BRAND_PROFILE,
          icon: generateImagePath(imagePathFromHub, userPermission),
          type: Svg,
        },
        {
          path: `${BRAND_GROUPS_PATH}/${brandId}`,
          name: sidebarScreenConstants.GROUPS,
          icon: generateImagePath(imagePathFromHub, groups),
          type: Svg,
        },
        {
          path: `${BRAND_THEME_SETTINGS_PATH}/${brandId}`,
          name: sidebarScreenConstants.THEME_SETTINGS,
          icon: generateImagePath(imagePathFromHub, themeSetting),
          type: Svg,
        },
        {
          path: `${BRAND_SURVEY_QUESTIONS_PATH}/${brandId}`,
          name: sidebarScreenConstants.SURVEY_QUESTIONS,
          icon: generateImagePath(imagePathFromHub, surveyQuestion),
          type: Svg,
        },
        {
          path: `${BRAND_ARCHIVED_QUESTIONS_PATH}/${brandId}`,
          name: sidebarScreenConstants.ARCHIVED_QUESTIONS,
          icon: generateImagePath(imagePathFromHub, archivedQuestions),
          type: Svg,
        },
      ],
    },
  ];

  if (isInboxActive(allBrandsWithFeatureFlags, brandId)) {
    navLinks.push({
      path: `${BRAND_INBOX_PATH}/${brandId}`,
      name: sidebarScreenConstants.INBOX,
      icon: faMailBulk,
    });
  }

  return navLinks;
};

//Group admin nav links
const getGroupNavLinks = ({
  groupId,
  user,
  imagePathFromHub,
  allBrandsWithFeatureFlags,
}) => {
  const navLinks = [
    {
      path: `${DASHBOARD_PATH}/${groupId}`,
      name: sidebarScreenConstants.DASHBOARD,
      icon: faChartLine,
    },
    {
      isDropdown: true,
      name: sidebarScreenConstants.SURVEY_MENU,
      icon: faListAlt,
      subMenu: [
        {
          path: `${GROUP_PROFILE_PATH}/${groupId}`,
          name: sidebarScreenConstants.GROUP_PROFILE,
          icon: generateImagePath(imagePathFromHub, userPermission),
          type: Svg,
        },
        {
          path: `${GROUP_MANAGERS_PATH}/${groupId}`,
          name: sidebarScreenConstants.GROUP_MANAGER,
          icon: faUsers,
        },
        {
          path: `${GROUP_SURVEY_QUESTIONS_PATH}/${groupId}`,
          name: sidebarScreenConstants.SURVEY_QUESTIONS,
          icon: generateImagePath(imagePathFromHub, surveyQuestion),
          type: Svg,
        },
        {
          path: `${GROUP_USER_PERMISSIONS}/${groupId}`,
          name: sidebarScreenConstants.USER_PERMISSIONS,
          icon: generateImagePath(imagePathFromHub, userPermission),
          type: Svg,
        },
      ],
    },
  ];

  if (isInboxActive(allBrandsWithFeatureFlags, user.brandId)) {
    navLinks.push({
      path: `${GROUP_INBOX_PATH}/${groupId}`,
      name: sidebarScreenConstants.INBOX,
      icon: faMailBulk,
    });
  }

  return navLinks;
};

//Manager admin nav links
const getManagerNavLinks = ({
  managerId,
  user,
  imagePathFromHub,
  allBrandsWithFeatureFlags,
}) => {
  const navLinks = [
    {
      path: `${DASHBOARD_PATH}/${managerId}`,
      name: sidebarScreenConstants.DASHBOARD,
      icon: faChartLine,
    },
    {
      isDropdown: true,
      name: sidebarScreenConstants.SURVEY_MENU,
      icon: faListAlt,
      subMenu: [
        {
          path: `${MANAGER_PROFILE_PATH}/${managerId}`,
          name: sidebarScreenConstants.MANAGER_PROFILE,
          icon: generateImagePath(imagePathFromHub, userPermission),
          type: Svg,
        },
        {
          path: `${MANAGER_SURVEY_QUESTIONS_PATH}/${managerId}`,
          name: sidebarScreenConstants.SURVEY_QUESTIONS,
          icon: generateImagePath(imagePathFromHub, surveyQuestion),
          type: Svg,
        },
        {
          path: `${MANAGER_USER_PERMISSIONS}/${managerId}`,
          name: sidebarScreenConstants.USER_PERMISSIONS,
          icon: generateImagePath(imagePathFromHub, userPermission),
          type: Svg,
        },
      ],
    },
  ];

  if (isInboxActive(allBrandsWithFeatureFlags, user.brandId)) {
    navLinks.push({
      path: `${MANAGER_INBOX_PATH}/${managerId}`,
      name: sidebarScreenConstants.INBOX,
      icon: faMailBulk,
    });
  }

  return navLinks;
};

const getGroupBrandAdminNavLinks = ({
  role,
  location,
  brandId,
  paramGroupId,
  imagePathFromHub,
}) => {
  if (role === LoginRoles.BRAND_ADMIN) {
    return [
      {
        path:
          location.state?.name === pathConstant.BRAND_PROFILE
            ? `${BRAND_PROFILE_PATH}/${brandId}/${GROUP_PROFILE_PAGE}/${paramGroupId}`
            : `${DASHBOARD_FROM_STORES_PATH}/${brandId}${BRAND_PERFORMANCE_PATH}/${GROUP_PROFILE_PAGE}/${paramGroupId}`,
        name: sidebarScreenConstants.PROFILE,
        icon: generateImagePath(imagePathFromHub, userPermission),
        type: Svg,
        state: location.state,
      },
      {
        path:
          location.state?.name === pathConstant.BRAND_PROFILE
            ? `${BRAND_PROFILE_PATH}/${brandId}/${GROUP_PERFORMANCE_PAGE}/${paramGroupId}`
            : `${DASHBOARD_FROM_STORES_PATH}/${brandId}${BRAND_PERFORMANCE_PATH}/${GROUP_PERFORMANCE_PAGE}/${paramGroupId}`,
        name: sidebarScreenConstants.PERFORMANCE,
        icon: generateImagePath(imagePathFromHub, performance),
        type: Svg,
        state: location.state,
      },
    ];
  }
  return [
    {
      path:
        location.state?.name === pathConstant.STORE_BRAND_PROFILE
          ? `${BRAND_PROFILE_PATH}/${brandId}/${GROUP_PROFILE_PAGE}/${paramGroupId}`
          : `${DASHBOARD_FROM_STORES_PATH}/${brandId}${BRAND_PERFORMANCE_PATH}/${GROUP_PROFILE_PAGE}/${paramGroupId}`,
      name: sidebarScreenConstants.PROFILE,
      icon: generateImagePath(imagePathFromHub, userPermission),
      type: Svg,
      state: location.state,
    },
    {
      path:
        location.state?.name === pathConstant.STORE_BRAND_PROFILE
          ? `${BRAND_PROFILE_PATH}/${brandId}/${GROUP_PERFORMANCE_PAGE}/${paramGroupId}`
          : `${DASHBOARD_FROM_STORES_PATH}/${brandId}${BRAND_PERFORMANCE_PATH}/${GROUP_PERFORMANCE_PAGE}/${paramGroupId}`,
      name: sidebarScreenConstants.PERFORMANCE,
      icon: generateImagePath(imagePathFromHub, performance),
      type: Svg,
      state: location.state,
    },
  ];
};

const getBrandLocationNavLinks = ({
  location,
  brandId,
  locationId,
  imagePathFromHub,
}) => {
  return [
    {
      path: getStoreProfilePath(location, brandId, locationId),
      name: sidebarScreenConstants.PROFILE,
      icon: generateImagePath(imagePathFromHub, userPermission),
      type: Svg,
      state: location.state,
    },
    {
      path: getStorePerformancePath(location, brandId, locationId),
      name: sidebarScreenConstants.PERFORMANCE,
      icon: generateImagePath(imagePathFromHub, performance),
      type: Svg,
      state: location.state,
    },
  ];
};

const getGroupLocationNavLinks = ({
  role,
  location,
  groupId,
  locationId,
  imagePathFromHub,
}) => {
  if (role === LoginRoles.GROUP) {
    return [
      {
        path: getGroupAdminStoreProfilePath(location, groupId, locationId),
        name: sidebarScreenConstants.PROFILE,
        icon: generateImagePath(imagePathFromHub, userPermission),
        type: Svg,
        state: location.state,
      },
      {
        path: getGroupAdminStorePerformancePath(location, groupId, locationId),
        name: sidebarScreenConstants.PERFORMANCE,
        icon: generateImagePath(imagePathFromHub, performance),
        type: Svg,
        state: location.state,
      },
    ];
  } else {
    return [
      {
        path: viewPathConstants.STORES,
        name: sidebarScreenConstants.RESTAURANTS,
        icon: generateImagePath(imagePathFromHub, stores),
        type: Svg,
      },
      {
        path: `${viewPathConstants.SURVEY_PROFILE_FOR_ADMIN_AND_GROUP}/${groupId}/${locationId}`,
        name: sidebarScreenConstants.PROFILE,
        icon: generateImagePath(imagePathFromHub, userPermission),
        type: Svg,
      },
      {
        path: `${viewPathConstants.PERFORMANCE}/${groupId}/${locationId}`,
        name: sidebarScreenConstants.PERFORMANCE,
        icon: generateImagePath(imagePathFromHub, performance),
        type: Svg,
      },
    ];
  }
};

const getManagerLocationNavLinks = ({
  location,
  managerId,
  locationId,
  imagePathFromHub,
}) => {
  return [
    {
      path:
        location.state?.name === pathConstant.MANAGER_PERFORMANCE
          ? `${DASHBOARD_PATH}/${managerId}${MANAGER_PERFORMANCE_PATH}${STORE_PROFILE_PATH}/${locationId}`
          : `${MANAGER_PROFILE_PATH}/${managerId}${STORE_PROFILE_PATH}/${locationId}`,
      name: sidebarScreenConstants.PROFILE,
      icon: generateImagePath(imagePathFromHub, userPermission),
      type: Svg,
      state: location.state,
    },
    {
      path:
        location.state?.name === pathConstant.MANAGER_PERFORMANCE
          ? `${DASHBOARD_PATH}/${managerId}${MANAGER_PERFORMANCE_PATH}${STORE_PERFORMANCE_PATH}/${locationId}`
          : `${MANAGER_PROFILE_PATH}/${managerId}${STORE_PERFORMANCE_PATH}/${locationId}`,
      name: sidebarScreenConstants.PERFORMANCE,
      icon: generateImagePath(imagePathFromHub, performance),
      type: Svg,
      state: location.state,
    },
  ];
};

const getStoreProfilePath = (location, brandId, locationId) => {
  switch (location.state?.name) {
    case pathConstant.STORE_BRAND_PROFILE:
    case pathConstant.BRAND_PROFILE:
      return `${BRAND_PROFILE_PATH}/${brandId}${STORE_PROFILE_PATH}/${locationId}`;
    case pathConstant.STORE_GROUP:
    case pathConstant.GROUP:
      return `${BRAND_GROUPS_PATH}/${brandId}${STORE_PROFILE_PATH}/${locationId}`;
    case pathConstant.STORE_PERFORMANCE:
    case pathConstant.PERFORMANCE:
      return `${DASHBOARD_FROM_STORES_PATH}/${brandId}${BRAND_PERFORMANCE_PATH}${STORE_PROFILE_PATH}/${locationId}`;
    default:
      return `${viewPathConstants.STORES}/${brandId}${STORE_PROFILE_PATH}/${locationId}`;
  }
};

const getStorePerformancePath = (location, brandId, locationId) => {
  switch (location.state?.name) {
    case pathConstant.STORE_BRAND_PROFILE:
    case pathConstant.BRAND_PROFILE:
      return `${BRAND_PROFILE_PATH}/${brandId}${STORE_PERFORMANCE_PATH}/${locationId}`;
    case pathConstant.STORE_GROUP:
    case pathConstant.GROUP:
      return `${BRAND_GROUPS_PATH}/${brandId}${STORE_PERFORMANCE_PATH}/${locationId}`;
    case pathConstant.STORE_PERFORMANCE:
    case pathConstant.PERFORMANCE:
      return `${DASHBOARD_FROM_STORES_PATH}/${brandId}${BRAND_PERFORMANCE_PATH}${STORE_PERFORMANCE_PATH}/${locationId}`;
    default:
      return `${viewPathConstants.STORES}/${brandId}/store-performance/${locationId}`;
  }
};

const getGroupAdminStoreProfilePath = (location, brandId, locationId) => {
  switch (location.state?.name) {
    case pathConstant.GROUP_PERFROMANCE:
      return `${DASHBOARD_PATH}/${brandId}${GROUP_PERFORMANCE_PATH}${STORE_PROFILE_PATH}/${locationId}`;
    case pathConstant.GROUP_MANAGER:
      return `${GROUP_MANAGERS_PATH}/${brandId}${STORE_PROFILE_PATH}/${locationId}`;
    default:
      return `${GROUP_PROFILE_PATH}/${brandId}${STORE_PROFILE_PATH}/${locationId}`;
  }
};

const getGroupAdminStorePerformancePath = (location, brandId, locationId) => {
  switch (location.state?.name) {
    case pathConstant.GROUP_PERFROMANCE:
      return `${DASHBOARD_PATH}/${brandId}${GROUP_PERFORMANCE_PATH}${STORE_PERFORMANCE_PATH}/${locationId}`;
    case pathConstant.GROUP_MANAGER:
      return `${GROUP_MANAGERS_PATH}/${brandId}${STORE_PERFORMANCE_PATH}/${locationId}`;
    default:
      return `${GROUP_PROFILE_PATH}/${brandId}${STORE_PERFORMANCE_PATH}/${locationId}`;
  }
};

const getClassName = (
  value,
  defaultClassName,
  alternateClassName = emptyString,
) => {
  return value ? defaultClassName : alternateClassName;
};

/** Shared callback used by sidebar nav links to collapse the mobile menu. */
export const createSidebarCollapseCallback = (setExpanded) => () => {
  setExpanded(false);
};

/** Map nav-link configs through the shared nav-link renderer. */
export const mapSidebarNavLinks = (
  links,
  renderNavLinkList,
  iconClassName = emptyString,
  onClickCallback,
) => {
  return links.map((link) => {
    return renderNavLinkList(link, iconClassName, onClickCallback);
  });
};

/** Shared storage/auth values used by sidebar container top headers. */
export const getSidebarContainerTopContext = (
  authenticationUser,
  brandProfile,
) => {
  return {
    localRole: getItemFromStorage(loginConstants.ROLE),
    brandName:
      getItemFromStorage(loginConstants.BRAND_NAME) || brandProfile?.name,
    hasAuthenticatedUser:
      Object.keys(authenticationUser || {}).length > EMPTY_ARRAY_LENGTH,
  };
};

//Function to check if inbox is active
export const isInboxActive = (allBrandsWithFeatures, brandId) => {
  return allBrandsWithFeatures?.some((brand) => {
    if (Number(brand.brandId) !== Number(brandId)) return false;

    return brand.stores?.some((store) => {
      return store.storeFeatures?.includes(featureFlagsEnums.INBOX);
    });
  });
};

export const isInStoreActive = (allBrandsWithFeatures, brandId) => {
  return (
    allBrandsWithFeatures?.length > 0 &&
    allBrandsWithFeatures?.some(
      (brand) =>
        Number(brand.brandId) === Number(brandId) &&
        brand.brandFeatures?.includes(featureFlagsEnums.IN_STORE),
    )
  );
};

export const npsFirstFeatureStatus = (allBrandsWithFeatures, brandId) => {
  const currentBrand = allBrandsWithFeatures?.find(
    (brand) => Number(brand.brandId) === Number(brandId),
  );
  return currentBrand &&
    currentBrand.brandFeatures?.includes(featureFlagsEnums.NPS_FIRST)
    ? currentBrand.npsStatus
    : npsFirstFeatureStatusEnums.NONE;
};

export {
  getClassName,
  getNavLinks,
  getBrandNavLinks,
  getGroupNavLinks,
  getManagerNavLinks,
  getBrandLocationNavLinks,
  getGroupLocationNavLinks,
  getManagerLocationNavLinks,
  getGroupBrandAdminNavLinks,
};
